import { Foundation } from '../types/Foundation';

// Backend API endpoint
const API_BASE_URL = '/api/foundations';

// Interface for paginated response
interface PaginatedResponse {
  foundations: Foundation[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Transform backend data model to frontend Foundation type
function transformToFoundation(data: any): Foundation {
  // Check if the data comes from the detailed API response, which includes foundationData
  if (data.basicInfo) {
    // Data is already in the detailed format from backend transformToDetailedFoundation
    return {
      id: data.id || '',
      name: data.basicInfo.name || '',
      purpose: data.basicInfo.purpose || '',
      region: data.basicInfo.location || '',
      contactInfo: data.contactInfo || 'Ingen kontaktinfo tillgänglig',
      address: data.address || undefined,
      yearEstablished: data.yearEstablished || undefined,
      capital: data.capital || undefined,
      website: data.website || undefined,
      email: data.email || undefined,
      applicationDate: data.applicationDate || undefined,
      originalId: data.originalId || '',
      firmaTeckning: data.governance?.decisionMaking || undefined,
      board: data.governance?.boardMembers || [],
      // Include the full foundation data structure
      foundationData: {
        basicInfo: data.basicInfo || {},
        governance: data.governance || {},
        financial: data.financial || {},
        legal: data.legal || {}
      }
    };
  }
  
  // Standard transformation for list view
  return {
    id: data.id || '',
    name: data.name || '',
    purpose: data.purpose || '',
    region: data.region || '',
    contactInfo: data.contactInfo || 'Ingen kontaktinfo tillgänglig',
    address: data.address || undefined,
    yearEstablished: data.yearEstablished || undefined,
    capital: data.capital || undefined,
    website: data.website || undefined,
    originalId: data.originalId || '',
    board: data.board || []
  };
}

/**
 * Get all foundations with pagination
 */
export async function getAllFoundations(page: number = 1, limit: number = 30): Promise<PaginatedResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}?page=${page}&limit=${limit}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      foundations: data.data.map(transformToFoundation),
      total: data.pagination.totalItems,
      page: data.pagination.page,
      limit: data.pagination.limit,
      hasMore: data.pagination.page < data.pagination.totalPages
    };
  } catch (error) {
    console.error('Error fetching all foundations:', error);
    throw error;
  }
}

/**
 * Search foundations
 */
export async function searchFoundations(query: string, page: number = 1, limit: number = 30, region?: string): Promise<PaginatedResponse> {
  try {
    let url = `${API_BASE_URL}?page=${page}&limit=${limit}`;
    
    // Add search query if provided
    if (query && query.trim() !== '') {
      url += `&search=${encodeURIComponent(query)}`;
    }
    
    // Add region filter if provided
    if (region && region.trim() !== '') {
      url += `&lan=${encodeURIComponent(region)}`;
    }
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      foundations: data.data.map(transformToFoundation),
      total: data.pagination.totalItems,
      page: data.pagination.page,
      limit: data.pagination.limit,
      hasMore: data.pagination.page < data.pagination.totalPages
    };
  } catch (error) {
    console.error('Error searching foundations:', error);
    throw error;
  }
}

/**
 * Get foundation by ID
 */
export async function getFoundationById(id: string): Promise<Foundation> {
  try {
    const response = await fetch(`${API_BASE_URL}/${id}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return transformToFoundation(data.data);
  } catch (error) {
    console.error(`Error fetching foundation with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Update foundation info (Note: This may require additional backend implementation)
 */
export async function updateFoundationInfo(id: string, updates: {
  hemsida?: string;
  kontakt_info?: string;
  adress?: string;
}): Promise<Foundation> {
  try {
    const response = await fetch(`${API_BASE_URL}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return transformToFoundation(data.data);
  } catch (error) {
    console.error(`Error updating foundation with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Trigger manual data refresh (optional)
 */
export async function refreshData(): Promise<{ message: string; count: number }> {
  try {
    // This endpoint would need to be implemented in the backend
    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error refreshing data:', error);
    throw error;
  }
}

/**
 * Get available regions (län)
 */
export async function getRegions(): Promise<string[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/regions`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching regions:', error);
    throw error;
  }
}

/**
 * Get statistics about foundations
 */
export async function getFoundationStats(): Promise<{
  totalCount: number;
  byRegion: { region: string; count: number }[];
  lastUpdate: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/stats`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching foundation stats:', error);
    throw error;
  }
}
